import { useEffect, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useHistoryStore } from '../store/historyStore.js'
import { saveGoalsData } from '../data/goalsApi.js'
import { saveMetaData } from '../data/metaApi.js'

/**
 * useUndo — provides undo action + keyboard binding (⌘Z / Ctrl+Z).
 *
 * Behavior:
 *   1. Pop the most recent snapshot from history.
 *   2. Restore both goals and meta caches to the snapshot values (instant UI).
 *   3. POST both /goals and /meta in parallel so the server state matches.
 *   4. If either POST fails, leave the cache rolled back and log — the
 *      user can retry. Network-level undo failures are rare in practice.
 *
 * Returns { undo, canUndo, lastLabel } for components that want to show
 * an Undo button or keyboard hint.
 */
export function useUndo() {
  const queryClient = useQueryClient()
  const { history, popSnapshot } = useHistoryStore()
  const lastLabel = history[history.length - 1]?.label
  const canUndo = history.length > 0

  const undo = useCallback(async () => {
    const snapshot = popSnapshot()
    if (!snapshot) return

    const { goalsBefore, metaBefore } = snapshot

    // Restore caches immediately (optimistic — the user sees state revert)
    if (goalsBefore !== undefined) {
      queryClient.setQueryData(['goals'], goalsBefore)
    }
    if (metaBefore !== undefined) {
      queryClient.setQueryData(['meta'], metaBefore)
    }

    // Persist to server (in parallel). We don't block the UI on this.
    const tasks = []
    if (goalsBefore !== undefined) tasks.push(saveGoalsData(goalsBefore))
    if (metaBefore !== undefined) tasks.push(saveMetaData(metaBefore))

    try {
      await Promise.all(tasks)
    } catch (err) {
      console.warn('[undo] Server save failed; cache still reverted', err)
    }
  }, [queryClient, popSnapshot])

  // Keyboard shortcut: ⌘Z on Mac, Ctrl+Z on Windows/Linux
  useEffect(() => {
    const onKey = (e) => {
      const isUndo =
        (e.metaKey || e.ctrlKey) &&
        !e.shiftKey &&
        (e.key === 'z' || e.key === 'Z')
      if (!isUndo) return
      // Don't hijack undo inside text inputs/textareas — let browser handle
      const target = e.target
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return
      }
      if (!canUndo) return
      e.preventDefault()
      undo()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [canUndo, undo])

  return { undo, canUndo, lastLabel }
}
